import React from 'react';
import ReactDOM from 'react-dom';
import ScheduleProviderCard from '@src/components/scheduling-provider-card/ScheduleProviderCard.jsx';
import ScheduleProviderCardUtils from '@src/js/provider-card-utils.js';
import { ResultType } from '@src/js/constants.js';
import LocationSelector from '@src/components/location-selector/location-selector.jsx';
import ConfirmationUtils from '@js/confirmation-utils.es6.js';
import DynamicAppointmentFormUtils from '@src/js/services/dynamic-appointment-form-utils.js';
import ApiUtils from '@js/api-utils.es6.js';
import SessionStorageUtils from '@js/utils/session-storage-utils.es6.js';
import UrlUtils from "@js/utils/url-utils.es6.js";
import {
  ATTRIBUTES,
  SEARCH_PARAM_KEYS,
  SESSION_STORAGE_KEYS,
  WORKFLOWS,
} from '@js/constants/constants-imports.es6.js';
import SchedulingResultsAppointmentConfirmationContentType from '@js/content-types/scheduling-results-appointment-confirmation-content-type.es6.js';
import {LuminisServiceUtils} from '@js/utils/utils-imports.es6.js';

export default class Confirmation {
  constructor() {
    this.urlParams = new URLSearchParams(window.location.href.substring(window.location.href.indexOf('?')));
    this.reservedTimeSlot = sessionStorage.getItem('reservedTimeSlot');
    this.epicDepartmentId = this.urlParams.get('epicDepartmentId');
    sessionStorage.setItem('epicDepartmentId', this.epicDepartmentId);
    this.epicProviderId = sessionStorage.getItem('epicProviderId');
    this.durationMins = sessionStorage.getItem('durationMins');
    this.npi = this.urlParams.get('npi');
    this.appointmentType = sessionStorage.getItem('appointmentType');
    if (this.npi) {
      this.resultType = ResultType.PROVIDERS;
    } else if (this.epicDepartmentId) {
      this.resultType = ResultType.LOCATIONS;
    } else {
      this.resultType = sessionStorage.getItem('resultType');
    }

    this.providerAllSlots = JSON.parse(sessionStorage.getItem('providerAllSlots'));
    if (this.providerAllSlots) {
      this.providerAllSlots = ScheduleProviderCardUtils.addFilteredSlots(this.providerAllSlots);
    }
    else {
      this.providerAllSlots = {
        'provider': {},
        'departmentAvailabilities': []
      }
    }
    this.currentMnemonic = this.urlParams.get('mnemonic');
    this.luminisServiceUtils = new LuminisServiceUtils();

    const workflow = this.urlParams.get(SEARCH_PARAM_KEYS.WORKFLOW);
    this.isSelectNewTimeWorkflow = workflow?.toLowerCase() === WORKFLOWS.SELECT_NEW_TIME.toLowerCase();
  }

  async initContainer($) {
    if (this.resultType === ResultType.PROVIDERS) {
      await this.renderProviderConfirmation();
    }
    else if(this.resultType === ResultType.LOCATIONS) {
      this.hideSchedulingWithSection();
      document.querySelector('.mnemonic-disclaimer').classList.add('hidden');
    } else {
      this.#redirectToErrorPage();
    }

    await this.renderMnemonicPicker($);

    DynamicAppointmentFormUtils.renderDatePicker(false, this.providerAllSlots);
    this.showHideLocationChanger();
    DynamicAppointmentFormUtils.visitTypeOnChange(this.providerAllSlots, false);

    if (this.isSelectNewTimeWorkflow) {
      document.querySelector('.choose-day-or-time').click();
    }
  }

  async fetchAvailabilitiesByMnemonic(startDate, endDate, errorRedirectPage) {
    const hasReferral = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.HAS_REFERRAL) ?? false;
    let mnemonicJson = [];

    if (this.resultType === ResultType.PROVIDERS) {
      mnemonicJson = await this.luminisServiceUtils.getProviderAvailabilities(this.npi, startDate, endDate, errorRedirectPage);
    }
    else if (this.resultType === ResultType.LOCATIONS) {
      const epicProviderId = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.EPIC_PROVIDER_ID);

      if (!this.currentMnemonic || !epicProviderId) {
        // TODO LH-2003 - Allow dynamic locations to the Luminis Health API to get providerallslots if not passed in
        this.#redirectToErrorPage();
      }

      // Note that we are not using getDepartmentAvailabilities here because we need to fetch location-based results for a single mnemonic,
      // not all scheduling options for a single location
      const availabilitiesByProvider = await this.luminisServiceUtils.getAvailabilitiesOptimized(this.currentMnemonic, startDate, endDate, hasReferral, errorRedirectPage);
      let departmentAvailabilities = [];

      if (availabilitiesByProvider) {
        availabilitiesByProvider.forEach(providerAvailability => {
          // Filter to the current provider's results. We don't currently have the ability to display results for
          // multiple providers on the confirmation form, even if it's location-based scheduling.
          if (providerAvailability.provider.id === epicProviderId) {
            departmentAvailabilities = departmentAvailabilities.concat(providerAvailability.departmentAvailabilities);
          }
        });

        // For location-based scheduling, we are only focused on one mnemonic
        mnemonicJson.push({
          visitReasonMnemonic: this.currentMnemonic,
          departmentAvailabilities: departmentAvailabilities,
        });
      }

    }
    else {
      this.#redirectToErrorPage();
    }

    return mnemonicJson;
  }

  async renderMnemonicPicker($) {
    let possibleLastSlotTaken = false;

    if (this.currentMnemonic && this.epicDepartmentId && Object.keys(this.providerAllSlots.departmentAvailabilities).length !== 0) {
      await this.setupInitialSelect2($);

      const numberOfAvailabilities = this.providerAllSlots.departmentAvailabilities.map(x => x.availablilities).flat().length;

      if (numberOfAvailabilities === 1) {
        possibleLastSlotTaken = true;
      }
    } else {
      // show loading box and hide section until loaded
      document.querySelector('.condition-loading-box').classList.remove('hidden');
      document.querySelector('.condition-type-fieldset').classList.add('hidden');
    }

    // get mnemonic for the current providers npi
    const errorRedirectPage = this.#getErrorRedirectPage();

    let startAndEndDates;

    if (this.resultType === ResultType.PROVIDERS) {
      startAndEndDates = await this.luminisServiceUtils.getProviderEligibleDates(this.npi, errorRedirectPage);
    }
    else if (this.resultType === ResultType.LOCATIONS) {
      startAndEndDates = await this.luminisServiceUtils.getMychartProviderData(this.currentMnemonic, errorRedirectPage);
    }
    else {
      this.#redirectToErrorPage();
    }

    const mnemonicJson = await this.fetchAvailabilitiesByMnemonic(startAndEndDates.startDate, startAndEndDates.endDate, errorRedirectPage);

    // need a flag here to determine if it was the potential last slot; if so no error
    if (mnemonicJson && mnemonicJson.length === 0 && !possibleLastSlotTaken) {
      console.error(mnemonicJson);
      this.#redirectToErrorPage();
    }

    const mnemonicList = await DynamicAppointmentFormUtils.buildMnemonicSelect(
      mnemonicJson,
      startAndEndDates,
      this.currentMnemonic,
      this.providerAllSlots);

    if (mnemonicList.length === 0 || (mnemonicList.length === 1 && mnemonicList[0] === '')) {
      console.error('This provider has no mnemonics that are not excluded.');
      this.#redirectToErrorPage();
    }

    const humanReadableMnemonic = await ApiUtils.makeMnemonicHumanReadable(this.currentMnemonic);
    this.setConditionTypeAndReasonForVisit(
      $, mnemonicList, humanReadableMnemonic);

    // force a trigger if we are loading this in but providerallslots is empty but we have a mnemonic
    if (this.currentMnemonic && Object.keys(this.providerAllSlots.departmentAvailabilities).length === 0) {
      const select2Instance = $('#field-form-element-condition').data('select2');
      const selectedOption = select2Instance.data()[0];
      this.triggerMnemonicSelected(selectedOption, this);
    }

    document.querySelector('.condition-loading-box').classList.add('hidden');
    document.querySelector('.condition-type-fieldset').classList.remove('hidden');
  }

  async setupInitialSelect2($) {
    const $condition = $('#field-form-element-condition');
    if (this.epicDepartmentId) {
      const currLocation = DynamicAppointmentFormUtils.getCurrentLocationDept(this.epicDepartmentId, this.providerAllSlots);
      const timesList = currLocation.availabilityByDays;
      const maxDate = timesList[timesList.length - 1][0];
      const minDate = timesList[0][0];
      const humanReadableMnemonic = await ApiUtils.makeMnemonicHumanReadable(this.currentMnemonic);

      $condition.select2({
        theme: 'classic',
        data: [{
          text: humanReadableMnemonic,
          id: this.currentMnemonic,
          selected: true,
          mnemonicJson: {
            'visitReasonMnemonic': this.currentMnemonic,
            'departmentAvailabilities': this.providerAllSlots.departmentAvailabilities
          },
          startDate: minDate,
          endDate: maxDate
        }],
        disabled: true
      });
    }
  }

  setConditionTypeAndReasonForVisit($, mnemonicList, currentMnemonicText) {
    DynamicAppointmentFormUtils.initConditionSelect(
      $,
      '#field-form-element-condition',
      mnemonicList,
      this.triggerMnemonicSelected,
      this);
    this.setReasonForVisitText(currentMnemonicText);

    if (mnemonicList.length === 1) {
      document.querySelector('.mnemonic-disclaimer').classList.add('hidden');
    }
  }

  setReasonForVisitText(currentMnemonicText) {
    const reasonForVisit = document.querySelector(
      '.field--name-field-reason-for-visit-label .field__item',
    );

    const reasonForVisitLabel = document.querySelector(
      '.field--name-field-reason-for-visit-label .field__item',
    );
    if (currentMnemonicText === undefined) {
      reasonForVisit.innerHTML = `${reasonForVisit.innerHTML}`;
    } else {
      reasonForVisit.innerHTML = `${reasonForVisitLabel.innerHTML} ${currentMnemonicText}`;
    }
  }

  triggerMnemonicSelected(event, thisReference) {
    const visitReasonMnemonic = event.id;
    UrlUtils.updateSearchParameter(SEARCH_PARAM_KEYS.MNEMONIC, visitReasonMnemonic, false);

    thisReference.providerAllSlots = DynamicAppointmentFormUtils.setupMnemonicSlots(event);
    const insuranceRequirement = event.mnemonicJson.departmentAvailabilities[0].availablilities[0].insuranceRequirement;
    const referralRequirement = event.mnemonicJson.departmentAvailabilities[0].availablilities[0].referralRequirement;
    const appointmentNotesRequired = event.mnemonicJson.departmentAvailabilities[0].availablilities[0].appointmentNotesRequired;

    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.INSURANCE_REQUIREMENT, insuranceRequirement);
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.REFERRAL_REQUIREMENT, referralRequirement);
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.APPOINTMENT_NOTES_REQUIRED, appointmentNotesRequired);

    const schedulingResultsAppointmentConfirmationContentType = new SchedulingResultsAppointmentConfirmationContentType();
    if (schedulingResultsAppointmentConfirmationContentType.isRendered) {
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateInsuranceRequirements();
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateReferralRequirements();
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateAppointmentNotesRequired();
    }

    thisReference.setReasonForVisitText(event.text);
    thisReference.clearLocationChanger();
    sessionStorage.setItem('appointmentType', '');
    sessionStorage.setItem('epicAppointmentType', '');
    ConfirmationUtils.clearVisitType();
    DynamicAppointmentFormUtils.disableVisitTypeBasedOnMnemonic(thisReference.providerAllSlots);
    DynamicAppointmentFormUtils.visitTypeOnChange(thisReference.providerAllSlots, false);
  }

  hideSchedulingWithSection() {
    document.querySelector('.field--name-field-h3').classList.add('hidden');
  }

  clearLocationChanger() {
    const previousLocation = sessionStorage.getItem('epicDepartmentId');

    // check number of new locations
    const newLocationCount = this.providerAllSlots.departmentAvailabilities.length;
    const firstLocation = this.providerAllSlots.departmentAvailabilities[0].department.id;
    if (newLocationCount === 1 && previousLocation === firstLocation) {
      // the location did not change and there is only one available. Don't clear locations in this case.
      DynamicAppointmentFormUtils.clearDatePickerAndVisitType(false, this.providerAllSlots);
    } else {
      Confirmation.updateLocation('', '', '', this.providerAllSlots);
      this.showHideLocationChanger(true);
    }
  }

  showHideLocationChanger(wasCleared) {
    const providerLocations = this.providerAllSlots.departmentAvailabilities;
    const changeLocationButton = document.querySelector('.change-location-button');

    // page was reloaded before a location was chosen
    const appointmentLocationJSON = sessionStorage.getItem('appointmentLocation');
    if (!appointmentLocationJSON || (JSON.parse(appointmentLocationJSON).deptEpicId === '' && !wasCleared)) {
      document.querySelector('.location-box .placeholder').classList.remove('hidden');
      document.querySelector('.location-box .location-name').classList.add('hidden');
      document.querySelector('.location-box .location-address').classList.add('hidden');
      wasCleared = true;
    }

    if (providerLocations.length <= 1 && !wasCleared ) {
      // hide the change location button and the disclaimer.
      document.querySelector('.location-disclaimer').classList.add('hidden');
      changeLocationButton.classList.add('hidden');
      return;
    }

    const changeLocationButtonText = changeLocationButton.querySelector('.text');
    const locationLabel = document.querySelector('.field--name-field-change-location-label .field__item').textContent;
    changeLocationButtonText.textContent = `${locationLabel} (${providerLocations.length})`;

    changeLocationButton.addEventListener('click', () => {
      this.openLocationSelector(changeLocationButton, providerLocations);
    });

    const placeholderLocationText = document.querySelector('.location-box .placeholder');
    placeholderLocationText.addEventListener('click', () => {
      this.openLocationSelector(changeLocationButton, providerLocations);
    });
  }

  openLocationSelector(changeLocationButton, providerLocations) {
    document.querySelector('.location-select-box-wrapper').classList.remove('hidden');
    changeLocationButton.classList.add('hidden');
    ReactDOM.render(
      <>
        <LocationSelector
          field_close_list_label={document.querySelector('.field--name-field-close-list-label .field__item').textContent}
          locations={providerLocations}
          selectedEpicId={sessionStorage.getItem('epicDepartmentId')}
          providerAllSlots={this.providerAllSlots}
          key={this.currentMnemonic}
        />
      </>,
      document.querySelector('.location-select-box-wrapper')
    );
    const closeButton = document.querySelector('.location-select-box .close-bar');
    closeButton.addEventListener('click', () => {
      document.querySelector('.location-select-box-wrapper').classList.add('hidden');
      document.querySelector('.change-location-button').classList.remove('hidden');
    });
  }

  static async updateLocation(newEpicId, name, address, providerAllSlots) {
    UrlUtils.updateSearchParameter(SEARCH_PARAM_KEYS.EPIC_DEPARTMENT_ID, newEpicId, false);

    // use session storage for updating the location
    sessionStorage.setItem('epicDepartmentId', newEpicId);
    // need to clear date and time and force them to choose a new date and time.
    document.querySelector('.location-select-box-wrapper').classList.add('hidden');
    document.querySelector('.change-location-button').classList.remove('hidden');

    if (newEpicId === '') {
      document.querySelector('.location-box .placeholder').classList.remove('hidden');
      document.querySelector('.location-box .location-name').classList.add('hidden');
      document.querySelector('.location-box .location-address').classList.add('hidden');
    } else {
      document.querySelector('.location-box .placeholder').classList.add('hidden');
      document.querySelector('.location-box .location-name').classList.remove('hidden');
      document.querySelector('.location-box .location-address').classList.remove('hidden');
    }

    const locationNameBox = document.querySelector('.location-box-to-be-updated .location-name');
    const locationAddressBox = document.querySelector('.location-box-to-be-updated .location-address');
    locationNameBox.innerHTML = name;
    locationAddressBox.innerHTML = address;

    const appointmentLocationJson = {
      title: name,
      address: address,
      deptEpicId: newEpicId
    };
    sessionStorage.setItem('appointmentLocation', JSON.stringify(appointmentLocationJson));
    DynamicAppointmentFormUtils.clearDatePickerAndVisitType(false, providerAllSlots);

    await Confirmation.#updatePatientInsuranceOptions();
  }

  static async #updatePatientInsuranceOptions() {
    const luminisServiceUtils = new LuminisServiceUtils();
    const schedulingResultsAppointmentConfirmationContentType = new SchedulingResultsAppointmentConfirmationContentType(luminisServiceUtils);
    const patientInsuranceOptionsParagraph = schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.patientInsuranceOptions.paragraph;
    await patientInsuranceOptionsParagraph.updatePatientCoverages();
  }

  async renderProviderConfirmation() {
    if (!this.provider) {
      this.provider = await ApiUtils.getProviderByNPIAndMapToConfirmation(this.npi);
      if (!this.provider) {
        this.#redirectToErrorPage();
      }
      this.epicProviderId = await this.luminisServiceUtils.getProviderEpicId(this.npi);
      sessionStorage.setItem('epicProviderId', this.epicProviderId);
      sessionStorage.setItem(
        'appointmentProvider',
        JSON.stringify(this.provider),
      );
    }
    const specList = ScheduleProviderCardUtils.getProviderSpecialtiesArray(this.provider);
    ReactDOM.render(
      <>
        <ScheduleProviderCard
          key={this.npi}
          name={this.provider.field_hero_banner.field_hero_banner_text.value}
          specialties={specList}
          alias={ScheduleProviderCardUtils.getProviderUrl(this.provider)}
          image={ScheduleProviderCardUtils.getProviderImage(this.provider)}
          isAffiliate={!this.provider.field_hero_banner.field_is_luminis_health_provider}
          npi={this.npi}
          ratingsLabel={this.provider.field_hero_banner.field_ratings_label ? this.provider.field_hero_banner.field_ratings_label.field_label : ''}
          noRatingsText={this.provider.field_hero_banner.field_no_ratings_link ? this.provider.field_hero_banner.field_no_ratings_link.field_link_block_link.title : ''}
          noRatingsLink={this.provider.field_hero_banner.field_no_ratings_link ? this.provider.field_hero_banner.field_no_ratings_link.field_link_block_link.uri.replace('internal:', '') : ''}
          luminisProviderLabel={this.provider.field_hero_banner.field_luminis_health_provider_la.field_label}
          luminisProviderTooltip={this.provider.field_hero_banner.field_luminis_health_provider_to.field_label}
          affiliateProviderLabel={this.provider.field_hero_banner.field_affiliated_provider_label.field_label}
          affiliateProviderTooltip={this.provider.field_hero_banner.field_affiliate_provider_toolti2.field_label}
          isAcceptingTelehealth={this.provider.field_offering_telehealth}
          isAcceptingTelehealthLabel={this.provider.field_hero_banner.field_offering_telehealth_label.field_label}
          isAcceptingNewPatients={this.provider.field_accepting_new_patients}
          acceptingNewPatientsLabel={this.provider.field_hero_banner.field_accepting_new_patients_lab.field_label}
        />
      </>,
      document.getElementById('provider-card-container-appt')
    )
  }

  #getErrorRedirectPage() {
    const schedulingResultsAppointmentConfirmationContentType = new SchedulingResultsAppointmentConfirmationContentType();
    const appointmentFormParagraph = schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph;

    const searchParams = new URL(window.location.href).searchParams;
    const workflow = searchParams.get(SEARCH_PARAM_KEYS.WORKFLOW);

    if (workflow) {
      const referredFromMyChart = workflow.toLowerCase() === WORKFLOWS.MYCHART.toLowerCase();

      if (referredFromMyChart) {
        return appointmentFormParagraph.linkNoAvailability.anchor.$(0).attr(ATTRIBUTES.HREF);
      }
    }

    return appointmentFormParagraph.linkGenericProviderError.anchor.$(0).attr(ATTRIBUTES.HREF);
  }

  #redirectToErrorPage() {
    window.location.href = this.#getErrorRedirectPage();
  }
}

($ => {
  window.addEventListener('load', async () => {
    if (document.querySelector('.node--type-scheduling-results-appointment-c')) {
      const confirmation = new Confirmation();
      confirmation.initContainer($);
    }
  })
})(jQuery, Drupal);
